import { z } from "zod";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

const SP_DESCRIPTION = "Story point value (1=Common, 2=Magic, 3=Rare, 5=Unique)";

export function buildUpdateTaskTool(connection: AgentConnection) {
  return defineTool(
    "update_task_plan",
    "Save the finalized plan and/or description to the current task. Use in Plan mode after alignment. For children use update_subtask; for title/tags/PR use update_task_properties.",
    {
      plan: z.string().optional().describe("The task plan in markdown"),
      description: z.string().optional().describe("Updated task description"),
    },
    async ({ plan, description }) => {
      try {
        await connection.call("updateTaskFields", {
          sessionId: connection.sessionId,
          plan,
          description,
        });
        return textResult("Task updated successfully.");
      } catch {
        return textResult("Failed to update task.");
      }
    },
  );
}

function buildCreateSubtaskTool(connection: AgentConnection) {
  return defineTool(
    "create_subtask",
    "Create a subtask under the current parent task. Use when breaking a complex parent into smaller pieces during planning. For post-task follow-ups use create_follow_up_task.",
    {
      title: z.string().describe("Subtask title"),
      description: z.string().optional().describe("Brief description"),
      plan: z.string().optional().describe("Implementation plan in markdown"),
      ordinal: z.number().optional().describe("Step/order number (0-based)"),
      storyPointValue: z.number().optional().describe(SP_DESCRIPTION),
    },
    async ({ title, description, plan, ordinal, storyPointValue }) => {
      try {
        const result = await connection.call("createSubtask", {
          sessionId: connection.sessionId,
          title,
          ...(description !== undefined && { description }),
          ...(plan !== undefined && { plan }),
          ...(storyPointValue !== undefined && { storyPointValue }),
          ...(ordinal !== undefined && { ordinal }),
        });
        return textResult(`Subtask created with ID: ${result.id}`);
      } catch (error) {
        return textResult(
          `Failed to create subtask: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

function buildUpdateSubtaskTool(connection: AgentConnection) {
  return defineTool(
    "update_subtask",
    "Update an existing subtask's fields (title, description, plan, ordinal, storyPointValue). Use when refining a child's plan or reordering. For the current task use update_task_plan.",
    {
      subtaskId: z.string().describe("The subtask ID to update"),
      title: z.string().optional(),
      description: z.string().optional(),
      plan: z.string().optional(),
      ordinal: z.number().optional(),
      storyPointValue: z.number().optional().describe(SP_DESCRIPTION),
    },
    async ({ subtaskId, title, description, plan, storyPointValue }) => {
      try {
        await connection.call("updateSubtask", {
          sessionId: connection.sessionId,
          subtaskId,
          ...(title !== undefined && { title }),
          ...(description !== undefined && { description }),
          ...(plan !== undefined && { plan }),
          ...(storyPointValue !== undefined && { storyPointValue }),
        });
        return textResult("Subtask updated.");
      } catch (error) {
        return textResult(`Failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    },
  );
}

function buildDeleteSubtaskTool(connection: AgentConnection) {
  return defineTool(
    "delete_subtask",
    "Delete a subtask by id. When to use: a subtask was created in error or is no longer needed. Returns: confirmation string.",
    { subtaskId: z.string().describe("The subtask ID to delete") },
    async ({ subtaskId }) => {
      try {
        await connection.call("deleteSubtask", {
          sessionId: connection.sessionId,
          subtaskId,
        });
        return textResult("Subtask deleted.");
      } catch (error) {
        return textResult(`Failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    },
  );
}

function buildListSubtasksTool(connection: AgentConnection) {
  return defineTool(
    "list_subtasks",
    "List all subtasks under the current parent task. Use to coordinate child work — check who is running, ready for review, or blocked. For non-child tasks use get_task.",
    {},
    async () => {
      try {
        const subtasks = await connection.call("listSubtasks", {
          sessionId: connection.sessionId,
        });
        return textResult(JSON.stringify(subtasks, null, 2));
      } catch {
        return textResult("Failed to list subtasks.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildPackTools(connection: AgentConnection) {
  return [
    defineTool(
      "start_child_cloud_build",
      "Start a cloud build (codespace) for a child task. Preconditions: child is in Open status, has a story point value, and has an agent assigned.",
      {
        childTaskId: z.string().describe("The child task ID to start a cloud build for"),
      },
      async ({ childTaskId }) => {
        try {
          const result = await connection.call("startChildCloudBuild", {
            sessionId: connection.sessionId,
            childTaskId,
          });
          return textResult(`Cloud build started for child task: ${result.childTaskId}`);
        } catch (error) {
          return textResult(
            `Failed to start child cloud build: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
    defineTool(
      "stop_child_build",
      "Send a graceful stop signal to a running child build's agent. Not a force-kill — the agent may take a moment to wind down.",
      {
        childTaskId: z.string().describe("The child task ID whose build should be stopped"),
      },
      async ({ childTaskId }) => {
        try {
          await connection.call("stopChildBuild", {
            sessionId: connection.sessionId,
            childTaskId,
          });
          return textResult(`Stop signal sent to child task: ${childTaskId}`);
        } catch (error) {
          return textResult(
            `Failed to stop child build: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
    defineTool(
      "approve_and_merge_pr",
      "Approve and merge a child task's PR. Preconditions: child in ReviewPR. Returns { merged }: true = merged (status→ReviewDev); false = automerge queued, wait for ReviewDev.",
      {
        childTaskId: z
          .string()
          .describe("The child task ID whose PR should be approved and merged"),
      },
      async ({ childTaskId }) => {
        try {
          const result = await connection.call("approveAndMergePR", {
            sessionId: connection.sessionId,
            childTaskId,
          });
          if (result.merged) {
            return textResult(
              `PR #${result.prNumber} approved and merged for task ${result.childTaskId}. Task status updated to ReviewDev.`,
            );
          }
          return textResult(
            `PR #${result.prNumber} merge queued for task ${result.childTaskId} — CI checks still in progress. The PR will auto-merge when all checks pass. Do NOT proceed as if merged. Wait for the child task status to change to ReviewDev before continuing.`,
          );
        } catch (error) {
          return textResult(
            `Failed to approve and merge PR: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
  ];
}

export function buildPmTools(
  connection: AgentConnection,
  options?: { includePackTools?: boolean },
) {
  const tools = [
    buildUpdateTaskTool(connection),
    buildCreateSubtaskTool(connection),
    buildUpdateSubtaskTool(connection),
    buildDeleteSubtaskTool(connection),
    buildListSubtasksTool(connection),
  ];
  if (!options?.includePackTools) return tools;
  return [...tools, ...buildPackTools(connection)];
}
